// src/pages/creator-panel/CallLogs/tabs/TransferTab.js
// Read-only display of the cold-transfer (human-handoff) dial outcome for a
// Live Phone Call. Populated from the Twilio <Dial action> status callback.
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

const SectionHeading = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, mt: 3 }}>
        {children}
    </Typography>
);

// Monospace value box matching the Analysis/Call Cost tab styling.
const ValueBox = ({ children }) => (
    <Box sx={{
        bgcolor: 'action.hover',
        border: '1px solid', borderColor: 'divider',
        borderRadius: 1, px: 1.5, py: 1,
        fontFamily: 'monospace', fontSize: 13, color: 'text.primary',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
        {children || '—'}
    </Box>
);

// Map the Twilio DialCallStatus (+ our redirect_* markers) to a chip color.
const STATUS_COLOR = {
    completed: 'success',
    answered: 'success',
    busy: 'error',
    'no-answer': 'warning',
    failed: 'error',
    canceled: 'warning',
    redirect_accepted: 'info',
    redirect_failed: 'error',
};

const STATUS_HELP = {
    completed: 'The clinic answered and the call connected, then ended normally.',
    answered: 'The clinic answered the call.',
    busy: 'The clinic line was busy.',
    'no-answer': 'The clinic did not answer before the timeout.',
    failed: 'The dial failed (often Twilio Geographic Permissions for the destination country, an unverified number on a trial account, or insufficient balance — check the Twilio Debugger).',
    canceled: 'The dial was canceled before it connected.',
    redirect_accepted: 'Twilio accepted the redirect and started dialing the clinic. The final answered/busy/failed outcome arrives when the dialed leg ends.',
    redirect_failed: 'Twilio rejected the redirect request itself.',
};

const formatDuration = (secs) => {
    const n = Number(secs);
    if (!Number.isFinite(n) || n <= 0) return '0s';
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60);
    const s = n % 60;
    return `${m}m ${s}s`;
};

const formatTs = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

const TransferTab = ({ session }) => {
    const status = session?.transfer_dial_status || '';
    const destination = session?.transfer_destination;
    const reason = session?.transfer_reason;
    const duration = session?.transfer_dial_duration;
    const dialSid = session?.transfer_dial_sid;
    const updatedAt = session?.transfer_updated_at;

    const hasTransfer = !!(status || destination || reason || dialSid || updatedAt) || duration != null;

    if (!hasTransfer) {
        return (
            <Box sx={{ pb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', mt: 2 }}>
                    No transfer occurred on this call. (Cold-transfer to a human is
                    Twilio-only; non-Twilio providers won't populate this tab.)
                </Typography>
            </Box>
        );
    }

    const statusColor = STATUS_COLOR[String(status).toLowerCase()] || 'default';

    return (
        <Box sx={{ pb: 2 }}>
            <SectionHeading>Dial Status</SectionHeading>
            <Chip
                label={status || '—'}
                color={statusColor}
                size="small"
                sx={{ fontWeight: 600 }}
            />
            {STATUS_HELP[String(status).toLowerCase()] && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {STATUS_HELP[String(status).toLowerCase()]}
                </Typography>
            )}

            <SectionHeading>Destination</SectionHeading>
            <ValueBox>{destination}</ValueBox>

            <SectionHeading>Reason</SectionHeading>
            <ValueBox>{reason}</ValueBox>

            <SectionHeading>Dial Duration</SectionHeading>
            <ValueBox>{duration != null ? formatDuration(duration) : '—'}</ValueBox>

            <SectionHeading>Dialed Leg SID</SectionHeading>
            <ValueBox>{dialSid}</ValueBox>

            <SectionHeading>Updated</SectionHeading>
            <ValueBox>{formatTs(updatedAt)}</ValueBox>
        </Box>
    );
};

export default TransferTab;
